import * as React from 'react';
import { ArrowUpRight, ArrowDownRight, Minus } from 'lucide-react';
import { cn } from '../lib/cn';

/**
 * KPICard — dashboard metric tile.
 * Used by podiatrist dashboard + admin dashboard.
 *
 * RTL: trend icons mirror correctly via rtl-flip; numbers stay LTR via tabular.
 */

export interface KPICardProps extends React.HTMLAttributes<HTMLDivElement> {
  label: React.ReactNode;
  value: React.ReactNode;
  unit?: React.ReactNode;
  /** Trend in percent — positive number = up, negative = down */
  trend?: number;
  trendLabel?: React.ReactNode;
  /** When true, an upward trend is "good" (most KPIs).
      Set false for KPIs where down is good (e.g. "open issues"). */
  trendUpIsGood?: boolean;
  icon?: React.ReactNode;
  accentColor?: 'brand' | 'success' | 'warning' | 'info' | 'clay';
}

const accentColors = {
  brand: 'text-brand-500 bg-brand-50',
  success: 'text-success-500 bg-success-50',
  warning: 'text-warning-500 bg-warning-50',
  info: 'text-info-500 bg-info-50',
  clay: 'text-clay-500 bg-clay-50',
};

export function KPICard({
  label,
  value,
  unit,
  trend,
  trendLabel,
  trendUpIsGood = true,
  icon,
  accentColor = 'brand',
  className,
  ...props
}: KPICardProps) {
  const hasTrend = typeof trend === 'number';
  const isUp = (trend ?? 0) > 0;
  const isDown = (trend ?? 0) < 0;
  const trendIsGood = (isUp && trendUpIsGood) || (isDown && !trendUpIsGood);
  const TrendIcon = isUp ? ArrowUpRight : isDown ? ArrowDownRight : Minus;
  const trendColor = trendIsGood ? 'text-success-600' : isUp || isDown ? 'text-error-600' : 'text-text-muted';

  return (
    <div
      className={cn(
        'rounded-lg border border-border-subtle bg-surface-elevated shadow-sm p-5',
        'flex flex-col gap-3',
        className,
      )}
      {...props}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="text-xs font-medium text-text-muted uppercase tracking-wide">
          {label}
        </div>
        {icon ? (
          <div
            className={cn(
              'h-9 w-9 rounded-md flex items-center justify-center shrink-0',
              accentColors[accentColor],
            )}
            aria-hidden="true"
          >
            {icon}
          </div>
        ) : null}
      </div>

      <div className="flex items-baseline gap-1">
        <span className="text-3xl font-semibold text-text-primary tabular leading-none">
          {value}
        </span>
        {unit ? <span className="text-sm text-text-muted">{unit}</span> : null}
      </div>

      {hasTrend ? (
        <div className="flex items-center gap-1.5 text-xs">
          <TrendIcon className={cn('h-3.5 w-3.5 rtl-flip', trendColor)} aria-hidden="true" />
          <span className={cn('font-medium tabular', trendColor)}>
            {Math.abs(trend!).toFixed(1)}%
          </span>
          {trendLabel ? <span className="text-text-muted">{trendLabel}</span> : null}
        </div>
      ) : null}
    </div>
  );
}
